import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import useGoalProgressData from "./data";
import { formatFull } from "./format";
import GoalPopover from "./GoalPopover";
import GoalTrendChart from "./GoalTrendChart";
import GoalShape from "./GoalShape";
import { periodWindowLabelTitle } from "../../helpers/periodWindowLabel";
import { offDayTotal, periodWordFor } from "../../helpers/workingDays";
import CardBusy from "../CardScope/CardBusy";
import "./goalProgress.css";

/* Build Your Own's single-value graphs — everything else in that picker
   (area/line/bar/spark) still plots the running total over time and stays
   in GoalTrendChart. */
const SHAPE_STYLES = ["number", "target", "ring", "donut", "gauge"];

/**
 * Goal Progress — pick a metric, set a goal, watch a running-total line climb
 * toward it. Kelvin's call (2026-09-21): a ring read as "just a percentage" —
 * he wanted a real line graph, flexible to whatever window the card is
 * pinned to (day / week / month / quarter / custom, the same ScopeWindow
 * every scoped card already has), with the goal drawn as a line on the same
 * Y axis rather than baked into a single number.
 *
 * Kelvin's follow-up (2026-09-28): the trend can now show each DAY on its own
 * instead of the running total ("it accumulates data, then going higher and
 * higher"), the axis can drop the days the company doesn't trade on, and the
 * goal line became a PACE line — the goal divided by the
 * period's working days, climbing across the month so it lands on the goal on
 * the last working day. Both live in Appearance; the arithmetic is in data.js
 * (paceDaysFor) and the drawing in GoalTrendChart.
 *
 * Hero number counts up (900ms, cubic ease-out) whenever the live amount
 * changes. Reduced motion jumps straight to the value. Reaching the goal for
 * the first time (not on mount, not on every render past it) flips the line
 * and endpoint to the "reached" palette and pulses the endpoint once.
 */
function useCountUp(target) {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const firstRef = useRef(true);
  useEffect(() => {
    const from = firstRef.current ? 0 : fromRef.current;
    fromRef.current = target;
    const reduced = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || from === target) {
      firstRef.current = false;
      setVal(target);
      return undefined;
    }
    firstRef.current = false;
    const t0 = performance.now();
    const DUR = 900;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / DUR);
      const eased = 1 - (1 - p) ** 3;
      setVal(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return val;
}

const IcTarget = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="0.6" fill="currentColor" />
  </svg>
);
const IcPencil = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const GoalProgress = ({ settings, title, boardWindow, boardCustom, blockId, saveSettings, workspaceId }) => {
  const s = settings || {};
  const data = useGoalProgressData(s, workspaceId, boardWindow, boardCustom);
  /* The admin's own goal wins; a derived metric with no goal typed falls back to
     the one its real anchor twin already carries (data.defaultGoal). */
  const goal = Number(s.goalTarget) || data.defaultGoal || 0;
  const hasGoal = goal > 0;
  const rawPct = hasGoal ? (data.amount / goal) * 100 : 0;
  const displayPct = Math.round(rawPct);
  const reached = hasGoal && rawPct >= 100;

  const animatedAmount = useCountUp(data.amount);

  const wasReached = useRef(reached);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (reached && !wasReached.current) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 1500);
      wasReached.current = reached;
      return () => clearTimeout(t);
    }
    wasReached.current = reached;
    return undefined;
  }, [reached]);

  const canEdit = !!(saveSettings && blockId);
  const triggerRef = useRef(null);
  const [popAnchor, setPopAnchor] = useState(null);
  const [draft, setDraft] = useState("");

  const openPopover = () => {
    if (!canEdit) return;
    const r = triggerRef.current?.getBoundingClientRect();
    setDraft(hasGoal ? String(goal) : "");
    setPopAnchor(r ? { top: r.top, bottom: r.bottom, left: r.left, right: r.right } : null);
  };
  const closePopover = () => setPopAnchor(null);
  const commitGoal = () => {
    if (canEdit) {
      const n = Number(draft);
      const clean = draft.trim() === "" || !Number.isFinite(n) || n <= 0 ? null : n;
      saveSettings(blockId, (opt) => ({ ...opt, goalTarget: clean }));
    }
    closePopover();
  };
  /* Writes straight through, same as any other quick-edit — no separate
     "confirm" step, so switching "For" while the goal number is still open
     previews against the new period immediately. */
  const changeWindow = canEdit ? (v) => saveSettings(blockId, (opt) => ({ ...opt, window: v })) : null;

  const label = title || data.label;
  const periodLabel = periodWindowLabelTitle(data.win, data.custom);
  const hide = s.hide || {};
  const chartStyle = s.chartStyle || "area";
  /* A rate or average has no running total: summing "$173 an hour" across the
     month is a number that means nothing, so a non-additive metric is always
     drawn a day at a time, whatever the saved view says. */
  /* Total Scheduled Revenue's bars are two-tone (completed on top of what is
     still scheduled) once the split has loaded — see GoalTrendChart. The
     legend is shown from the moment the metric is picked, while it is still
     loading, so its row of height is never added under bars that are already
     drawn (the chart would shrink and the bars animate between two layouts). */
  const splitBars = chartStyle === "bar" && (data.splitStatus === "loading" || data.splitStatus === "ready");
  const chartView = (s.chartView === "daily" || !data.additive) ? "daily" : "cumulative";
  const goalLineMode = s.goalLineMode === "flat" ? "flat" : "pace";
  const chartDays = s.chartDays === "working" ? "working" : "all";
  /* A metric with no day-by-day history (Beat the Yeti rate) has nothing for a
     trend to plot; it is drawn as a gauge instead of an apology. */
  let shapeStyle = null;
  if (SHAPE_STYLES.includes(chartStyle)) shapeStyle = chartStyle;
  else if (data.seriesless) shapeStyle = "gauge";
  const isTrend = !shapeStyle;
  /* The caption under the chart only has anything to say where the pace line
     itself is drawn — see GoalTrendChart's own `withPace`, which these four
     conditions mirror. A percentage goal has no per-day figure (a rate is not
     earned a day at a time), and a shape style has no chart to caption. */
  /* Average Daily Revenue is the one non-additive metric that still has a
     pace worth stating: its goal is already per day, so the figure is the goal
     itself, held against the revenue taken so far (see data.js). The chart keeps
     its flat goal line; only this caption and the pill are added. */
  const avgPace = data.avgDaily && goal > 0 && data.workingDays > 0;
  const showPace = isTrend
    && !hide.goalline
    && !hide.pace
    && data.kind !== "%"
    && (avgPace || (goalLineMode === "pace" && data.additive && data.dailyPace > 0));
  const dailyPace = avgPace ? goal : data.dailyPace;
  const expectedToDate = avgPace ? goal * data.workingDaysElapsed : data.expectedToDate;
  /* Where the period should be by the end of today, against where it is. Both
     figures come from the same divisor the line is drawn at, so the pill can
     never disagree with the chart beside it. */
  const paceGap = showPace && expectedToDate != null ? data.amountToDate - expectedToDate : null;
  const periodNoun = periodWordFor(data.win);
  const paceState = (() => {
    if (paceGap == null) return null;
    /* Within a whole day's pace either way is "on pace" — a card that flips
       between Ahead and Behind on a $3 rounding is noise, not news. */
    if (Math.abs(paceGap) < dailyPace) return "on";
    return paceGap > 0 ? "ahead" : "behind";
  })();

  /* ── What came in on a day the chart isn't showing ──────────────────────
     "Working days only" drops the weekend and holiday columns, and in the
     DAY-BY-DAY view that takes their takings off the chart with them — a
     Saturday's $70 is on no bar, because rolling it onto Monday would make
     Monday's bar a lie about Monday. The running-total view has no such
     problem: hidden or not, every day is already inside the total the line is
     drawing, which is why Monday's step jumps by the whole weekend.

     So in the one case where money really is off the chart, the card states
     it, and the bars plus this figure reconcile to the number above them.
     Never silently dropped and never misattributed to a day that didn't earn
     it. */
  const offDayAmount = isTrend && data.additive && chartDays === "working" && chartView === "daily"
    ? offDayTotal(data.dailySeries, data.periodDays)
    : 0;
  /* Same part toggle as the pace line's own caption — one row under the chart,
     switched on or off as a whole. */
  const showOffDays = offDayAmount > 0 && !hide.pace;

  return (
    <div className="gmbw-sales-card gprog">
      <CardBusy active={data.loading} cover label="Loading…" />
      <div className="gmbw-sales-head">
        <span className="gmbw-sales-title">{label || "Goal progress"}</span>
        {hasGoal && data.hasMetric && !hide.pct && (
          <span className={`gprog-pill${reached ? " hit" : ""}${celebrate ? " celebrate" : ""}`}>
            {reached ? "Goal reached" : `${displayPct}%`}
          </span>
        )}
      </div>

      <div className="gmbw-sales-body gprog-body">
        {/* Loading has its own card-level veil (CardBusy) — nothing to say
            here, and no flashing "Pick a metric" while a configured one is
            still resolving. */}
        {!data.loading && !data.hasMetric && (
          <div className="gprog-empty">
            {IcTarget}
            <b>Pick a metric</b>
            <span>Open this card&apos;s editor and choose what to track a goal against.</span>
          </div>
        )}
        {!data.loading && data.hasMetric && (
          <>
            <div className="gprog-top">
              <div className="gprog-hero">
                <span className="gprog-hero-val">{formatFull(animatedAmount, data.kind)}</span>
                <span className="gprog-hero-sub" title={!Number(s.goalTarget) && data.goalSource ? `Goal from ${data.goalSource}` : undefined}>
                  {hasGoal ? `of ${formatFull(goal, data.kind)} goal` : "no goal set"}
                  {!hide.period && <span className="gprog-hero-period"> · {periodLabel}</span>}
                </span>
              </div>

              <div className="gprog-goalctl">
                {(() => {
                  if (hasGoal) {
                    return (
                      <button
                        ref={triggerRef}
                        type="button"
                        className="gprog-goalpill"
                        disabled={!canEdit}
                        aria-label="Edit goal"
                        onClick={openPopover}
                      >
                        <span>{reached ? "Past goal by" : "Remaining"}</span>
                        <b>{formatFull(Math.abs(goal - data.amount), data.kind)}</b>
                        {canEdit && IcPencil}
                      </button>
                    );
                  }
                  if (canEdit) {
                    return <button ref={triggerRef} type="button" className="gprog-setgoal" onClick={openPopover}>Set goal</button>;
                  }
                  return <span className="gprog-nogoal">Not set</span>;
                })()}
              </div>
            </div>

            {shapeStyle ? (
              <GoalShape
                style={shapeStyle}
                amount={data.amount}
                goal={goal}
                kind={data.kind}
                reached={reached}
                color={s.lineColor || "var(--gprog-brand)"}
              />
            ) : (
              <GoalTrendChart
                series={data.dailySeries}
                completedSeries={data.completedSeries}
                earnedSeries={data.earnedSeries}
                aheadDays={data.aheadDays}
                periodDays={data.periodDays}
                dailyPace={data.dailyPace}
                goal={goal}
                kind={data.kind}
                reached={reached}
                color={s.lineColor || "var(--gprog-brand)"}
                goalColor={s.goalColor || "var(--gprog-goal)"}
                style={chartStyle}
                view={chartView}
                goalLineMode={goalLineMode}
                days={chartDays}
                yStep={s.yStep}
                additive={data.additive}
                showGoalLine={!hide.goalline}
                showTooltip={!hide.tooltip}
              />
            )}

            {splitBars && (
              <div className="gprog-legend">
                <span title="Jobs booked for this day that have since been completed, at the amount they were booked for. They may have finished on a different day, so this is not the revenue earned that day.">
                  <i style={{ background: "var(--gprog-good)" }} />Now completed
                </span>
                <span><i style={{ background: "var(--gprog-open)" }} />Still scheduled</span>
              </div>
            )}

            {/* Look-ahead: what is already booked for the days past the period.
                Rendered from the moment the option is on (a placeholder while
                the figures load) so the row never appears under bars that have
                already drawn, which would shrink the chart beneath them. */}
            {data.aheadDays > 0 && (
              <div className="gprog-ahead">
                <b>{`Next ${data.aheadDays} days`}</b>
                <span>{data.loading ? "loading…" : `${formatFull(data.aheadAmount, data.kind)} already booked`}</span>
                <em>not in the total above</em>
              </div>
            )}

            {/* One row under the chart, for whatever the chart itself can't
                say. The pace: what the dashed line is (the goal spread over the
                period's working days) and whether today is keeping up — a line
                nobody can name is a line nobody trusts, and the divisor is the
                one number an admin will want to check. Then anything earned on
                a day this chart isn't drawing, so the bars and the number above
                them always add up. */}
            {(showPace || showOffDays) && (
              <div className="gprog-pace">
                {showPace ? (
                  <span className="gprog-pace-key">
                    <i style={{ background: s.goalColor || "var(--gprog-goal)" }} />
                    <b>{formatFull(dailyPace, data.kind)}</b>
                    <span>
                      {`per working day · ${data.workingDays} ${periodNoun ? `this ${periodNoun}` : "in this range"}`}
                      {chartDays === "working" ? " · working days only" : ""}
                    </span>
                  </span>
                ) : (
                  <span className="gprog-pace-key">
                    <span>Working days only</span>
                  </span>
                )}
                {showOffDays && (
                  <span className="gprog-pace-off">
                    {`+ ${formatFull(offDayAmount, data.kind)} off-days`}
                  </span>
                )}
                {paceState && (
                  <span className={`gprog-pace-state ${paceState}`}>
                    {paceState === "on" && "On pace"}
                    {paceState === "ahead" && `Ahead by ${formatFull(Math.abs(paceGap), data.kind)}`}
                    {paceState === "behind" && `Behind by ${formatFull(Math.abs(paceGap), data.kind)}`}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {popAnchor && (
        <GoalPopover
          anchor={popAnchor}
          value={draft}
          kind={data.kind}
          window={s.window}
          onWindowChange={changeWindow}
          onValueChange={setDraft}
          onCommit={commitGoal}
          onCancel={closePopover}
        />
      )}
    </div>
  );
};

GoalProgress.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  title: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  blockId: PropTypes.string,
  saveSettings: PropTypes.func,
  workspaceId: PropTypes.string,
};
GoalProgress.defaultProps = {
  settings: {},
  title: null,
  boardWindow: null,
  boardCustom: null,
  blockId: null,
  saveSettings: null,
  workspaceId: null,
};

export default GoalProgress;
